/**
 * Lead contact consent (#1440) — what the widget sends with a lead.
 *
 * Two boxes (`ConsentCheckbox`), each granting its own purpose:
 *  - `request` — REQUIRED, on a form's first screen: the dealer may contact
 *    the visitor about this request, including through its AI assistant
 *    (`contact_ai`);
 *  - `marketing` — optional, on the last screen: offers on similar cars
 *    (`contact_marketing`).
 * The earlier single optional box (wording `v1`) granted both.
 *
 * Only references travel: `{ purpose, granted, wordingVersion, channels,
 * locale }`. The text itself is resolved by b2b-api from the same version +
 * locale (`lead-consent-wording.registry.ts`), which is kept identical to the
 * i18n JSON by a parity spec — we never send the text.
 */
import { getSelectedLanguage } from '../i18n/selectedLanguage';
import { translate } from '../i18n/translate';
import { useOptionalI18n } from '../i18n/I18nContext';

/**
 * The boxes: the wording version each renders (`leadConsent.<version>.text`)
 * and the purposes ticking it grants.
 */
export const CONSENT_BOXES = {
  request: { version: 'v2_request', purposes: ['contact_ai'], required: true },
  marketing: { version: 'v2_marketing', purposes: ['contact_marketing'], required: false },
};

/**
 * A box's spec. A missing or unknown `box` is the required request box: a
 * caller that forgets the prop then gets a form that can't be sent until the
 * box is ticked — noticed at once — instead of a crashed form.
 *
 * @param {string} [box]
 */
export const consentBox = (box) => CONSENT_BOXES[box] ?? CONSENT_BOXES.request;

/** Channels the wording names. */
export const CONSENT_CHANNELS = ['email', 'phone', 'sms', 'whatsapp'];

/** Locales b2b-api has the wording in. */
export const CONSENT_LOCALES = ['en', 'fr', 'de', 'lu', 'ru', 'el'];

/** Form UI (labels, buttons) is English, so English is the no-choice default. */
export const DEFAULT_CONSENT_LOCALE = 'en';

/**
 * Locale to render and record the consent in: explicit override → the
 * surrounding `I18nProvider`'s language → the buyer's explicit choice in the
 * language selector → English.
 *
 * @param {{ override?: string|null, providerLanguage?: string|null }} [opts]
 * @returns {string}
 */
export function resolveConsentLocale({ override, providerLanguage } = {}) {
  for (const candidate of [override, providerLanguage, getSelectedLanguage()]) {
    if (typeof candidate === 'string' && CONSENT_LOCALES.includes(candidate)) return candidate;
  }
  return DEFAULT_CONSENT_LOCALE;
}

/**
 * `consents` for `submitLeadFromForm`: one entry per purpose of each ticked
 * box. Nothing ticked → `undefined` (the field is omitted): not ticking is not
 * a withdrawal and nothing is recorded.
 *
 * @param {{ request?: boolean, marketing?: boolean }} ticked
 * @param {string} locale
 * @returns {Array<object>|undefined}
 */
export function buildLeadConsents(ticked, locale) {
  const resolvedLocale = CONSENT_LOCALES.includes(locale) ? locale : DEFAULT_CONSENT_LOCALE;
  const consents = Object.entries(CONSENT_BOXES)
    .filter(([box]) => ticked?.[box] === true)
    .flatMap(([, { version, purposes }]) =>
      purposes.map((purpose) => ({
        purpose,
        granted: true,
        wordingVersion: version,
        channels: [...CONSENT_CHANNELS],
        locale: resolvedLocale,
      }))
    );
  return consents.length > 0 ? consents : undefined;
}

/**
 * Locale the consent is rendered AND recorded in (#1440). The form that
 * submits the lead must use the same value for `buildLeadConsents`, so the
 * text b2b-api records is the text the visitor read — hence a hook shared by
 * the form and this component rather than a value computed inside it.
 *
 * @param {string} [override] — force a locale (else provider → explicit choice → en).
 */
export function useConsentLocale(override) {
  const i18n = useOptionalI18n();
  return resolveConsentLocale({ override, providerLanguage: i18n?.language });
}

/**
 * A box's consent text for a locale, with `{dealer}` filled in. Same
 * substitution rule as b2b-api's `resolveConsentWording`: blank name →
 * localized "the dealer".
 *
 * @param {string} locale
 * @param {string|null} dealerName
 * @param {'request'|'marketing'} box
 */
export function consentText(locale, dealerName, box) {
  const template = translate(locale, `leadConsent.${consentBox(box).version}.text`) ?? '';
  const name =
    (typeof dealerName === 'string' && dealerName.trim()) ||
    translate(locale, 'leadConsent.dealerFallback') ||
    '';
  return template.split('{dealer}').join(name);
}
